import React from "react";
import StarRating from "../star-rating/star-rating";
import { BsFillCartCheckFill, BsStarFill } from "react-icons/bs";
import { MdOutlineInventory2 } from "react-icons/md";

const ModernCategoryGrid = ({
  title,
  subtitle,
  items = [],
  type = "account",
  totalItems,
  filters,
  onFilterChange,
  onApplyFilters,
  onResetFilters,
}) => {
  const isTopup = type === "topup";
  const isGame = type === "game";
  const itemCount = Number.isFinite(totalItems) ? totalItems : items.length;

  return (
    <section className="mgc-shell">
      <div className="mgc-head">
        <div className="mgc-head-copy">
          <h1 className="mgc-head-title">{title}</h1>
          <p className="mgc-head-subtitle">{subtitle}</p>
        </div>
        <div className="mgc-count-chip">
          <MdOutlineInventory2 className="mgc-count-chip-icon" />
          <span>{itemCount} sản phẩm</span>
        </div>
      </div>

      {filters && onFilterChange && (
        <div className="mgc-filter-bar">
          <input
            value={filters.search}
            onChange={(e) => onFilterChange("search", e.target.value)}
            className="mgc-filter-input mgc-filter-search"
            placeholder="Tìm theo tên hoặc mã sản phẩm"
          />

          <input
            value={filters.minPrice}
            onChange={(e) => onFilterChange("minPrice", e.target.value)}
            className="mgc-filter-input"
            placeholder="Giá từ"
            type="number"
            min="0"
          />

          <input
            value={filters.maxPrice}
            onChange={(e) => onFilterChange("maxPrice", e.target.value)}
            className="mgc-filter-input"
            placeholder="Giá đến"
            type="number"
            min="0"
          />

          <select
            value={filters.minRating}
            onChange={(e) => onFilterChange("minRating", e.target.value)}
            className="mgc-filter-select"
          >
            <option value="">Mọi đánh giá</option>
            <option value="5">Từ 5 sao</option>
            <option value="4">Từ 4 sao</option>
            <option value="3">Từ 3 sao</option>
            <option value="2">Từ 2 sao</option>
            <option value="1">Từ 1 sao</option>
          </select>

          <select
            value={filters.sort}
            onChange={(e) => onFilterChange("sort", e.target.value)}
            className="mgc-filter-select"
          >
            <option value="newest">Mới nhất</option>
            <option value="price_asc">Giá tăng dần</option>
            <option value="price_desc">Giá giảm dần</option>
            <option value="sold_desc">Bán chạy</option>
            <option value="rating_desc">Đánh giá cao</option>
          </select>

          <button onClick={onApplyFilters} className="mgc-filter-btn mgc-filter-btn--primary">
            Lọc
          </button>

          <button onClick={onResetFilters} className="mgc-filter-btn mgc-filter-btn--ghost">
            Xóa lọc
          </button>
        </div>
      )}

      {items.length > 0 ? (
        <div className={`mgc-grid ${isTopup ? "mgc-grid--topup" : ""} ${isGame ? "mgc-grid--game" : ""}`}>
          {items.map((item) => (
            <ModernCategoryCard key={item?._id} item={item} type={type} />
          ))}
        </div>
      ) : (
        <div className="mgc-empty">
          <p className="mgc-empty-title">Hiện tại chưa có sản phẩm</p>
          <p className="mgc-empty-subtitle">Vui lòng quay lại sau để xem thêm sản phẩm mới.</p>
        </div>
      )}
    </section>
  );
};

export const ModernCategoryCard = ({ item, type }) => {
  const soldAmount = Number(item?.product_sold_amount || 0);
  const rating = Number(item?.averageRating || 0);
  const isTopup = type === "topup";
  const isGame = type === "game";

  return (
    <a
      href={`/product/${item?.product_slug}`}
      className={`mgc-card ${isTopup ? "mgc-card--topup" : ""} ${isGame ? "mgc-card--game" : ""}`}
    >
      <div className="mgc-card-media-wrap">
        <img
          loading="lazy"
          className="mgc-card-media"
          src={item?.product_imgs?.[0]}
          alt={item?.product_name || "product"}
        />
      </div>

      <div className="mgc-card-body">
        <p className="mgc-card-name">{item?.product_name || "Sản phẩm"}</p>

        <div className="mgc-card-meta-row">
          {rating > 0 ? (
            <StarRating rating={rating} />
          ) : (
            <p className="mgc-no-rating-label">
              <BsStarFill className="mgc-no-rating-icon" />
              Chưa có đánh giá
            </p>
          )}
          <p className="mgc-sold-label">{soldAmount > 0 ? `` : "Mới"}</p>
        </div>

        <div className="mgc-card-bottom-row">
          <p className="mgc-status-label">
            <BsFillCartCheckFill className="mgc-status-icon" />
            {item?.product_status === "active" ? "Còn hàng" : "Hết hàng"}
          </p>
          <p className="mgc-price-label">{getPriceLabel(item, type)}</p>
        </div>
      </div>
    </a>
  );
};

const getPriceLabel = (item, type) => {
  if (type === "topup") {
    const prices = item?.product_attributes?.packages?.map((pkg) => pkg?.price || 0) || [];
    if (!prices.length) return "0đ";
    const minPrice = Math.min(...prices);
    const maxPrice = Math.max(...prices);

    if (minPrice === maxPrice) {
      return `${formatCompactPrice(minPrice)}đ`;
    }

    return `${formatCompactPrice(minPrice)}đ ~ ${formatCompactPrice(maxPrice)}đ`;
  }

  const price = item?.product_attributes?.price || 0;
  return `${price.toLocaleString()}đ`;
};

const formatCompactPrice = (value) => {
  if (value >= 1_000_000_000) {
    return `${trimDecimal(value / 1_000_000_000)}T`;
  }
  if (value >= 1_000_000) {
    return `${trimDecimal(value / 1_000_000)}Tr`;
  }
  if (value >= 1_000) {
    return `${trimDecimal(value / 1_000)}K`;
  }
  return value.toLocaleString("vi-VN");
};

const trimDecimal = (num) => {
  return Number.isInteger(num) ? `${num}` : num.toFixed(1);
};

export default ModernCategoryGrid;
